import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, forwardRef, viewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { IconComponent } from '@examdojo/core/icon';
import {
  SelectOptionDirective,
  SelectOptionType,
  SelectTriggerDirective,
  SingleSelectComponent,
} from '@examdojo/core/select';
import { StopPropagationDirective } from '@examdojo/core/stop-propagation';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { UnwrapArray } from '@examdojo/core/typescript';
import { TiptapNodeViewContentDirective } from 'ngx-tiptap';
import { v4 as uuid } from 'uuid';
import { SimpleCalloutBlock, SimpleCalloutVariant } from '../../../model';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { SIMPLE_CALLOUT_VARIANT_CONFIG, SimpleCalloutVariantOption } from './simple-callout-color.config';
import { DEFAULT_SIMPLE_CALLOUT_PROPS } from './simple-callout.model';

@Component({
  selector: 'dojo-simple-callout',
  standalone: true,
  imports: [
    NgClass,
    IconComponent,
    TiptapNodeViewContentDirective,
    StopPropagationDirective,
    SingleSelectComponent,
    SelectOptionDirective,
    SelectTriggerDirective,
  ],
  templateUrl: './simple-callout.component.html',
  styleUrls: ['./simple-callout-shared.scss', './simple-callout.component.scss'],
  hostDirectives: [DragWithHandleOnlyDirective],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => SimpleCalloutComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimpleCalloutComponent extends NodeViewComponent<SimpleCalloutBlock> {
  constructor() {
    super();

    // Update title content reactively, but skip while the user is editing
    effect(() => {
      const title = this.title();
      const element = this.titleElement()?.nativeElement;
      if (element && !this.isEditingTitle) {
        element.textContent = title;
      }
    });

    // Update variant control when variant attribute changes
    effect(() => {
      const currentVariant = this.variant();
      if (this.variantCtrl.value !== currentVariant) {
        this.variantCtrl.setValue(currentVariant);
      }
    });
  }

  private readonly titleElement = viewChild<ElementRef<HTMLSpanElement>>('titleElement');
  private isEditingTitle = false;

  readonly variantOptions: SimpleCalloutVariantOption[] = Object.values(SimpleCalloutVariant).map((variant) => ({
    label: SIMPLE_CALLOUT_VARIANT_CONFIG[variant].label,
    value: variant,
    pickerColor: SIMPLE_CALLOUT_VARIANT_CONFIG[variant].pickerColor,
  }));

  readonly selectOptionType!: SelectOptionType<UnwrapArray<typeof this.variantOptions>>;

  readonly variantCtrl = new FormControl<SimpleCalloutVariant>(SimpleCalloutVariant.Concept, { nonNullable: true });

  readonly title = this.getProp('title');
  readonly variant = this.getProp('variant');
  readonly customCss = this.getProp('custom_css');

  readonly isEditable = computed(() => this.editor().isEditable);

  readonly colorClasses = computed(() => {
    const variant = this.variant();
    return SIMPLE_CALLOUT_VARIANT_CONFIG[variant].color;
  });

  readonly icon = computed(() => {
    const variant = this.variant();
    return SIMPLE_CALLOUT_VARIANT_CONFIG[variant].icon;
  });

  override getDefaults = (): SimpleCalloutBlock => ({
    ...DEFAULT_SIMPLE_CALLOUT_PROPS,
    id: uuid(),
  });

  onTitleFocus(): void {
    this.isEditingTitle = true;
  }

  onTitleBlur(): void {
    this.isEditingTitle = false;
  }

  updateTitle(event: Event): void {
    const target = event.target as HTMLElement;
    const newTitle = target.textContent || '';
    this.updateProp('title', newTitle);
  }

  updateVariant(variant: SimpleCalloutVariant): void {
    this.updateProp('variant', variant);
    this.updateProp('title', SIMPLE_CALLOUT_VARIANT_CONFIG[variant].label);
  }
}
